import { useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import BBSLogo from "@/components/BBSLogo";
import { Button } from "@/components/ui/button";

/**
 * Printable one-page trilingual synopsis (English + Hindi + Bengali).
 * Ctrl/Cmd + P → "Save as PDF" produces a clean handout for the shop floor.
 * QR code links back to the running Asset IQ instance.
 */
export default function Synopsis() {
  const navigate = useNavigate();
  const appUrl = typeof window !== "undefined" ? window.location.origin : "";

  // Ensure light theme when printing regardless of user's dark-mode setting
  useEffect(() => {
    const wasDark = document.documentElement.classList.contains("dark");
    document.documentElement.classList.remove("dark");
    return () => {
      if (wasDark) document.documentElement.classList.add("dark");
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 py-6 print:bg-white print:py-0" data-testid="synopsis-page">
      {/* Non-printable toolbar */}
      <div className="max-w-[820px] mx-auto mb-4 flex items-center justify-between print:hidden">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          data-testid="synopsis-back-btn"
          className="gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Button>
        <Button
          onClick={() => window.print()}
          data-testid="synopsis-print-btn"
          className="gap-2 bg-[#7C1D3A] hover:bg-[#6c1c36]"
        >
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </Button>
      </div>

      {/* Printable card */}
      <article
        className="synopsis-sheet bg-white mx-auto shadow-sm print:shadow-none"
        style={{ width: "820px", maxWidth: "100%" }}
        data-testid="synopsis-sheet"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-10 pt-8 pb-4 border-b-2 border-[#7C1D3A]">
          <div>
            <BBSLogo size={54} />
            <div className="mt-2 flex items-center gap-2">
              <span className="h-1 w-8 bg-[#7C1D3A] rounded-full inline-block" />
              <span className="font-heading text-[10px] font-bold tracking-[0.3em] text-slate-700 uppercase">
                Internal App · Made by BBS for BBS
              </span>
            </div>
          </div>
          <div className="text-right">
            <h1 className="font-heading text-3xl font-bold text-[#7C1D3A] leading-none">Asset IQ</h1>
            <div className="text-[11px] text-slate-500 mt-1">Fixed Asset Management System</div>
          </div>
        </header>

        {/* Body — three-column trilingual layout */}
        <section className="grid grid-cols-3 gap-6 px-10 py-6 text-[10.5px] leading-snug text-slate-800">
          {/* ENGLISH */}
          <div>
            <h2 className="font-heading font-bold text-[12px] text-[#7C1D3A] mb-2 uppercase tracking-wider">
              English
            </h2>
            <p className="mb-2">
              <span className="font-bold">Asset IQ</span> is BBS Global&apos;s in-house
              Fixed Asset Management system — one real-time view of every machine,
              spare, consumable and piece of equipment across our garment
              manufacturing operations. Works on desktop, laptop, tablet and mobile.
            </p>
            <div className="font-bold mt-2 mb-1">Modules</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Asset Registry with depreciation (SLM &amp; WDV)</li>
              <li>Purchase Orders (Indian FY numbering)</li>
              <li>Consumables, Spares &amp; Jigs</li>
              <li>Maintenance &amp; AMC alerts (Email + SMS)</li>
              <li>Transfer, Write-off &amp; Physical Audit</li>
              <li>Statutory Reports (FAR, Depreciation, Maint.)</li>
              <li>QR labels + mobile scan</li>
              <li>AI Assistant &amp; smart search</li>
              <li>Role-based access</li>
            </ul>
            <div className="font-bold mt-2 mb-1">Benefits</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>One source of truth across teams</li>
              <li>Real-time visibility for management</li>
              <li>Compliance-ready statutory reports</li>
              <li>Prevents silent losses &amp; leakages</li>
              <li>30-second breakdown logging on floor</li>
            </ul>
          </div>

          {/* HINDI */}
          <div lang="hi">
            <h2 className="font-heading font-bold text-[12px] text-[#7C1D3A] mb-2 uppercase tracking-wider">
              हिन्दी
            </h2>
            <p className="mb-2">
              <span className="font-bold">Asset IQ</span> BBS Global का अपना Fixed
              Asset Management सिस्टम है — हर मशीन, स्पेयर, कंज़्यूमेबल और उपकरण की
              एक ही जगह रीयल-टाइम जानकारी। डेस्कटॉप, लैपटॉप, टैबलेट और मोबाइल — सब पर चलता है।
            </p>
            <div className="font-bold mt-2 mb-1">मॉड्यूल</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Asset Registry — SLM व WDV डेप्रिसिएशन</li>
              <li>Purchase Orders — भारतीय FY नंबरिंग</li>
              <li>Consumables, Spares व Jigs</li>
              <li>Maintenance व AMC अलर्ट (Email + SMS)</li>
              <li>Transfer, Write-off व Physical Audit</li>
              <li>Statutory रिपोर्ट्स (FAR, Depreciation)</li>
              <li>QR लेबल + मोबाइल स्कैन</li>
              <li>AI असिस्टेंट व स्मार्ट सर्च</li>
              <li>रोल-आधारित एक्सेस</li>
            </ul>
            <div className="font-bold mt-2 mb-1">लाभ</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>सभी टीमों के लिए एक ही सत्य स्रोत</li>
              <li>मैनेजमेंट को रीयल-टाइम दृश्यता</li>
              <li>Compliance के लिए तैयार रिपोर्ट्स</li>
              <li>चुपचाप होने वाला नुकसान बंद</li>
              <li>तकनीशियन 30 सेकंड में ब्रेकडाउन लॉग करें</li>
            </ul>
          </div>

          {/* BENGALI */}
          <div lang="bn">
            <h2 className="font-heading font-bold text-[12px] text-[#7C1D3A] mb-2 uppercase tracking-wider">
              বাংলা
            </h2>
            <p className="mb-2">
              <span className="font-bold">Asset IQ</span> হল BBS Global-এর নিজস্ব
              Fixed Asset Management সিস্টেম — প্রতিটি মেশিন, স্পেয়ার, কনজিউমেবল
              ও যন্ত্রপাতির একটিই রিয়েল-টাইম ভিউ। ডেস্কটপ, ল্যাপটপ, ট্যাবলেট ও মোবাইল সবেতেই চলে।
            </p>
            <div className="font-bold mt-2 mb-1">মডিউল</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>Asset Registry — SLM ও WDV অবচয়</li>
              <li>Purchase Orders — ভারতীয় FY নম্বরিং</li>
              <li>Consumables, Spares ও Jigs</li>
              <li>Maintenance ও AMC অ্যালার্ট (Email + SMS)</li>
              <li>Transfer, Write-off ও Physical Audit</li>
              <li>Statutory রিপোর্ট (FAR, Depreciation)</li>
              <li>QR লেবেল + মোবাইল স্ক্যান</li>
              <li>AI অ্যাসিস্ট্যান্ট ও স্মার্ট সার্চ</li>
              <li>রোল-ভিত্তিক অ্যাক্সেস</li>
            </ul>
            <div className="font-bold mt-2 mb-1">সুবিধা</div>
            <ul className="list-disc pl-4 space-y-0.5">
              <li>সব টিমের জন্য এক সত্যের উৎস</li>
              <li>ম্যানেজমেন্টের রিয়েল-টাইম দৃশ্যমানতা</li>
              <li>Compliance-এর জন্য প্রস্তুত রিপোর্ট</li>
              <li>নীরব ক্ষতি ও লিকেজ বন্ধ</li>
              <li>টেকনিশিয়ান ৩০ সেকেন্ডে ব্রেকডাউন লগ করেন</li>
            </ul>
          </div>
        </section>

        {/* Footer with QR */}
        <footer className="flex items-center justify-between px-10 py-5 border-t border-slate-200 bg-slate-50">
          <div className="max-w-[500px]">
            <div className="font-heading text-[13px] font-bold text-slate-900">
              Scan to open Asset IQ
            </div>
            <div className="text-[10.5px] text-slate-600 mt-1">
              Open the camera on your phone, point at the QR code, and tap the
              link that appears. Sign in with your company credentials to begin.
            </div>
            <div className="text-[9.5px] text-slate-500 mt-2 font-mono break-all">
              {appUrl}
            </div>
            <div className="text-[9.5px] text-slate-400 mt-3">
              © BBS Global Pvt. Ltd. · For internal use only
            </div>
          </div>
          <div className="bg-white p-2 border border-slate-200 rounded" data-testid="synopsis-qr">
            <QRCodeSVG
              value={appUrl || "https://asset-iq.local"}
              size={110}
              level="M"
              includeMargin={false}
              fgColor="#7C1D3A"
            />
          </div>
        </footer>
      </article>

      {/* Print-specific tweaks */}
      <style>{`
        @media print {
          @page { size: A4; margin: 10mm; }
          body { background: white !important; }
          .synopsis-sheet { width: 100% !important; box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}
